import { crmApi } from '../crmApi';
import {
    CustomerDetail,
    CancelResponse,
    TransactionRefundResponse,
    RefundResponse,
    RefundPayload,
    SearchSummary,
    CustomerNotes,
    CustomerNotesPayload,
    CreateNotePayload,
} from '../../types';

const DEV_URI = process.env.REACT_APP_CUSTOMER_SERVICE
    ? `${process.env.REACT_APP_CUSTOMER_SERVICE}`
    : '/customer-service';

const customersApi = crmApi.injectEndpoints({
    endpoints: (builder) => ({
        getSearchSummary: builder.query<SearchSummary[], number[]>({
            query: (ids) => `${DEV_URI}/customers/searchSummary?customerIds=${ids.join(',')}`,
            providesTags: ['SearchSummary'],
        }),
        getCustomer: builder.query<CustomerDetail, number>({
            query: (customerId) => `${DEV_URI}/customers/${customerId}`,
            providesTags: ['CustomerDetail'],
        }),
        getCustomerNotes: builder.query<CustomerNotes, CustomerNotesPayload>({
            query: (payload) =>
                `${DEV_URI}/customers/${payload.customerId}/notes?sort=createdDate,desc&page=${payload.pageNumber}&size=${payload.pageSize}`,
            providesTags: ['CustomerNotes'],
        }),
        createCustomerNote: builder.mutation<void, CreateNotePayload>({
            query: (payload) => ({
                url: `${DEV_URI}/customers/${payload.customerId}/notes`,
                method: 'POST',
                body: payload.text,
            }),
            invalidatesTags: ['CustomerNotes'],
        }),
        cancelMembership: builder.mutation<CancelResponse, number>({
            query: (customerId) => ({
                url: `${DEV_URI}/customers/${customerId}/cancel`,
                method: 'GET',
            }),
            invalidatesTags: ['CustomerDetail'],
        }),
        updateEmail: builder.mutation<CancelResponse, { email: string; customerId: number }>({
            query: (payload) => ({
                url: `${DEV_URI}/customers/${payload.customerId}`,
                method: 'PUT',
                body: { email: payload.email },
            }),
            onQueryStarted(payload, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    customersApi.util.updateQueryData('getCustomer', +payload.customerId, (draft) => {
                        draft.email = payload.email;
                    })
                );
                queryFulfilled.catch(patchResult.undo);
            },
        }),
        refundTransactions: builder.mutation<TransactionRefundResponse[], RefundPayload>({
            query: (pkg) => ({
                url: `${DEV_URI}/transactions/orders/${pkg.id}/refund?transactions=${pkg.transactions.join()}`,
                method: 'GET',
            }),
            invalidatesTags: ['CustomerDetail'],
        }),
        sendRefundEmail: builder.mutation<RefundResponse, RefundPayload>({
            query: (pkg) => ({
                url: `${DEV_URI}/transactions/orders/${pkg.id}/sendRefundEmail?transactions=${pkg.transactions.join()}`,
                method: 'GET',
            }),
            invalidatesTags: ['CustomerNotes'],
        }),
        sendCancelEmail: builder.mutation<CancelResponse, number>({
            query: (customerId) => ({
                url: `${DEV_URI}/customers/${customerId}/sendCancelEmail`,
                method: 'GET',
            }),
            invalidatesTags: ['CustomerNotes'],
        }),
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetCustomerQuery,
    useGetCustomerNotesQuery,
    useCancelMembershipMutation,
    useRefundTransactionsMutation,
    useGetSearchSummaryQuery,
    useCreateCustomerNoteMutation,
    useSendRefundEmailMutation,
    useUpdateEmailMutation,
    useSendCancelEmailMutation,
} = customersApi;
